import {
  CHARGERS_PER_STATION,
  CHARGER_POWER_KW,
  LA_MAP_CENTER,
  STEPS_PER_DAY,
  STATIONS,
  VEHICLE_COUNT,
} from "./constants";
import type {
  ChargingStationAgent,
  SimulationState,
  StationId,
  TripPlan,
  VehicleAgent,
} from "./types";

class SeededRandom {
  private seed: number;

  constructor(seed: number) {
    this.seed = seed;
  }

  next() {
    this.seed = (this.seed * 1664525 + 1013904223) >>> 0;
    return this.seed / 4294967296;
  }

  int(min: number, max: number) {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const randomLaDestination = (rng: SeededRandom): [number, number] => {
  // Spread destinations in a wide LA metro box.
  const lat = LA_MAP_CENTER[0] + (rng.next() * 0.32 - 0.16);
  const lng = LA_MAP_CENTER[1] + (rng.next() * 0.5 - 0.25);
  return [Number(lat.toFixed(6)), Number(lng.toFixed(6))];
};

const generateTripsForVehicle = (
  rng: SeededRandom,
  vehicleId: string,
  energyRateKWhPerKm: number,
): TripPlan[] => {
  const tripCount = rng.int(1, 3);
  const trips: TripPlan[] = [];
  let nextEarliestDeparture = rng.int(8, 24);

  for (let i = 0; i < tripCount; i += 1) {
    const departureStep = clamp(
      nextEarliestDeparture + rng.int(0, 24),
      8,
      STEPS_PER_DAY - 20,
    );
    const duration = rng.int(6, 20);
    const returnStep = clamp(departureStep + duration, departureStep + 4, STEPS_PER_DAY - 1);
    const distanceKm = rng.int(12, 60);
    const energyConsumptionKWh = Number((distanceKm * energyRateKWhPerKm).toFixed(2));

    trips.push({
      id: `${vehicleId}-trip-${i + 1}`,
      departureStep,
      returnStep,
      distanceKm,
      energyConsumptionKWh,
      destinationCoord: randomLaDestination(rng),
      completed: false,
    });

    nextEarliestDeparture = returnStep + rng.int(10, 28);
    if (nextEarliestDeparture > STEPS_PER_DAY - 20) {
      break;
    }
  }

  return trips;
};

const createVehicle = (index: number, rng: SeededRandom): VehicleAgent => {
  const id = `EV-${String(index + 1).padStart(2, "0")}`;
  const homeStationId: StationId = index < VEHICLE_COUNT / 2 ? "stationA" : "stationB";
  const batteryCapacityKWh = rng.int(75, 110);
  const initialSocRatio = rng.int(60, 92) / 100;
  const socKWh = Number((batteryCapacityKWh * initialSocRatio).toFixed(2));
  const energyRateKWhPerKm = Number((rng.int(15, 28) / 100).toFixed(2));
  const trips = generateTripsForVehicle(rng, id, energyRateKWhPerKm);

  return {
    id,
    homeStationId,
    batteryCapacityKWh,
    socKWh,
    energyConsumptionRateKWhPerKm: energyRateKWhPerKm,
    trips,
    currentState: "idle",
    activeTripId: null,
    position: { zone: "station", stationId: homeStationId },
  };
};

const createStation = (stationId: StationId): ChargingStationAgent => ({
  id: stationId,
  chargers: Array.from({ length: CHARGERS_PER_STATION }, (_, chargerIndex) => ({
    id: `${stationId}-charger-${chargerIndex + 1}`,
    powerKw: CHARGER_POWER_KW,
    vehicleId: null,
  })),
  waitingQueue: [],
  chargingVehicleIds: [],
  demandProfileKw: [],
});

export const createInitialSimulationState = (seed = 20260512): SimulationState => {
  const rng = new SeededRandom(seed);
  const vehicles = Array.from({ length: VEHICLE_COUNT }, (_, index) => createVehicle(index, rng));
  const stations = STATIONS.reduce<Record<StationId, ChargingStationAgent>>((acc, stationId) => {
    acc[stationId] = createStation(stationId);
    return acc;
  }, {} as Record<StationId, ChargingStationAgent>);

  return {
    step: 0,
    totalSteps: STEPS_PER_DAY,
    completed: false,
    running: false,
    vehicles,
    stations,
    chargingEventLog: [],
  };
};
